import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const IMGS = [
  "/images/hero.png",
  "/images/line1_cage.png",
  "/images/line2_radar.png",
  "/images/line3_highway.png",
  "/images/material_macro.png",
  "/images/night_drone.png",
];

export const Gallery = ({ t }) => {
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const next = useCallback(() => setI((p) => (p + 1) % IMGS.length), []);
  const prev = useCallback(() => setI((p) => (p - 1 + IMGS.length) % IMGS.length), []);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [next, prev, navigate]);

  return (
    <div data-testid="gallery" className="fixed inset-0 z-[70] bg-black flex flex-col select-none">
      <div className="flex items-center justify-between px-6 md:px-10 h-16 border-b border-[#272A2E] shrink-0">
        <p className="text-xs font-mono tracking-[0.2em] uppercase text-[#FF2A00]">// {t.galleryPage.title}</p>
        <div className="flex items-center gap-6">
          <span data-testid="gallery-counter" className="text-xs font-mono text-[#8A8F98]">{String(i + 1).padStart(2, "0")} / {String(IMGS.length).padStart(2, "0")}</span>
          <span className="hidden md:block text-[10px] font-mono tracking-[0.15em] text-[#4A4F58]">{t.galleryPage.hint}</span>
          <Link to="/" data-testid="gallery-close" className="text-[#8A8F98] hover:text-[#FF2A00] transition-colors duration-300"><X size={20} /></Link>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={i}
            src={IMGS[i]}
            alt={t.galleryPage.captions[i]}
            data-testid="gallery-image"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1.06 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 0.5 }, scale: { duration: 9, ease: "linear" } }}
            className="absolute inset-0 w-full h-full object-contain"
          />
        </AnimatePresence>
        <button onClick={prev} data-testid="gallery-prev" aria-label="prev"
          className="absolute left-4 top-1/2 -translate-y-1/2 border border-[#272A2E] bg-[#050505]/70 p-4 text-[#8A8F98] hover:text-[#FF2A00] hover:border-[#FF2A00] transition-colors duration-300">
          <ChevronLeft size={22} />
        </button>
        <button onClick={next} data-testid="gallery-next" aria-label="next"
          className="absolute right-4 top-1/2 -translate-y-1/2 border border-[#272A2E] bg-[#050505]/70 p-4 text-[#8A8F98] hover:text-[#FF2A00] hover:border-[#FF2A00] transition-colors duration-300">
          <ChevronRight size={22} />
        </button>
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent px-6 md:px-10 pb-6 pt-16">
          <p data-testid="gallery-caption" className="text-sm font-mono tracking-tight text-[#C8CCD2]">{t.galleryPage.captions[i]}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 px-6 md:px-10 py-4 border-t border-[#272A2E] shrink-0 overflow-x-auto">
        {IMGS.map((src, n) => (
          <button key={src} onClick={() => setI(n)} data-testid={`gallery-thumb-${n}`}
            className={`shrink-0 border transition-colors duration-300 ${n === i ? "border-[#FF2A00]" : "border-[#272A2E] hover:border-[#8A8F98]"}`}>
            <img src={src} alt="" className={`h-12 w-20 object-cover ${n === i ? "opacity-100" : "opacity-40 grayscale"}`} />
          </button>
        ))}
        <span className="ml-auto self-center pl-6 text-[10px] font-mono tracking-[0.15em] uppercase text-[#4A4F58] whitespace-nowrap" data-testid="gallery-owner">
          {t.owner.name} — {t.owner.email}
        </span>
      </div>
    </div>
  );
};
